import { useState, useRef } from "react";
import "@/App.css";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowDown, Menu, X } from "lucide-react";
import { AnimatedSection } from "@/components/AnimatedSection";
import ProductPage from "@/components/ProductPage";
import products from "@/data/productData";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/51325761-8706-4ecb-92e4-dada8b5adadb/images/e225de3e79bead62bd55c108b1b5af96995afc508d054b7f53ab1d4bbd29f9e3.png";

function Header({ onScrollToProducts }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border/40"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 flex items-center justify-between h-16">
        <div className="flex items-center gap-3" data-testid="brand-logo">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0052FF] to-[#FF5A5F] flex items-center justify-center">
            <span className="text-white font-heading font-bold text-sm">P</span>
          </div>
          <span className="font-heading font-bold text-lg text-foreground tracking-tight">
            Priyashaa <span className="text-muted-foreground font-normal text-sm">for Signpost India</span>
          </span>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          <button
            onClick={onScrollToProducts}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            data-testid="nav-products"
          >
            Product Ideas
          </button>
        </nav>
        <button
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/40 bg-white px-6 py-4 space-y-3" data-testid="mobile-menu">
          <button
            onClick={() => { onScrollToProducts(); setMobileMenuOpen(false); }}
            className="block text-sm font-medium text-foreground"
            data-testid="mobile-nav-products"
          >
            Product Ideas
          </button>
        </div>
      )}
    </header>
  );
}

function HeroMain({ onScrollToProducts }) {
  return (
    <section className="relative overflow-hidden bg-white" data-testid="main-hero">
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-semibold text-[#0052FF] mb-6">
              Product Vision & Roadmap
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-foreground leading-[1.02]">
              What I'd Build at{" "}
              <span className="gradient-text">Signpost India</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-6 max-w-lg">
              Hi, I'm Priyashaa. Here are five AI product concepts I've designed for Signpost, each with detailed workflows, technical architecture, and measurable outcomes.
            </p>
            <div className="mt-10">
              <button
                onClick={onScrollToProducts}
                className="bg-[#0052FF] text-white px-8 py-4 rounded-full font-medium text-sm hover:bg-[#0042cc] transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2"
                data-testid="hero-explore-btn"
              >
                View Product Ideas <ArrowDown className="w-4 h-4" />
              </button>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.3} className="relative hidden lg:block">
            <div className="absolute -inset-8 bg-gradient-to-br from-[#0052FF]/10 to-[#FF5A5F]/10 rounded-3xl blur-3xl gentle-pulse" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
              <img
                src={HERO_IMAGE}
                alt="AI-Powered OOH Solutions"
                className="w-full aspect-square object-cover"
                loading="eager"
              />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-foreground text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0052FF] to-[#FF5A5F] flex items-center justify-center">
                <span className="text-white font-heading font-bold text-sm">P</span>
              </div>
              <span className="font-heading font-bold text-lg">Priyashaa</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              Product portfolio for Signpost India. Five AI-powered concepts designed to
              transform OOH advertising operations, sales, and client servicing.
            </p>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-sm mb-4 text-white/80">Product Ideas</h4>
            <ul className="space-y-2">
              {products.map((p) => (
                <li key={p.id}>
                  <span className="text-sm text-white/50">
                    {p.tabLabel}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-xs text-white/40">
            &copy; {new Date().getFullYear()} Built by Priyashaa for Signpost India
          </span>
          <span className="text-xs text-white/40">
            Product & Workflow Portfolio
          </span>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const [activeTab, setActiveTab] = useState(products[0].id);
  const productsRef = useRef(null);

  const scrollToProducts = () => {
    productsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    window.scrollTo({ top: productsRef.current?.offsetTop - 120, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white font-body">
      <Header onScrollToProducts={scrollToProducts} />
      <HeroMain onScrollToProducts={scrollToProducts} />

      {/* Products Section */}
      <div ref={productsRef} id="products" className="scroll-mt-20">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Sticky Tab Navigation */}
          <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-b border-border/40">
            <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
              <TabsList
                className="flex w-full bg-transparent h-auto p-0 overflow-x-auto hide-scrollbar gap-0"
                data-testid="product-tabs-list"
              >
                {products.map((product) => (
                  <TabsTrigger
                    key={product.id}
                    value={product.id}
                    className="tab-active-indicator px-5 py-4 text-sm font-medium text-muted-foreground bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:text-[#0052FF] data-[state=active]:border-[#0052FF] data-[state=active]:shadow-none transition-all whitespace-nowrap hover:text-foreground"
                    data-testid={`tab-${product.id}`}
                  >
                    {product.tabLabel}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          {/* Tab Content */}
          {products.map((product) => (
            <TabsContent key={product.id} value={product.id} className="mt-0 focus-visible:ring-0 focus-visible:ring-offset-0">
              <ProductPage product={product} />
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Footer />
    </div>
  );
}

export default App;
